import { Link } from 'react-router-dom';
import { Button, Card, Space, Tag } from '@/shared/ui/primitives';
import type { WhatsAppUpdateSuggestion } from '../api/aiAds.api';
import './whatsapp-updates.css';

type Props = {
  suggestions: WhatsAppUpdateSuggestion[];
  loading?: boolean;
  title?: string;
  emptyText?: string;
  onRefresh?: () => void;
  onApply: (id: number) => void;
  onIgnore: (id: number) => void;
  busy?: boolean;
};

export function adPath(suggestion: Pick<WhatsAppUpdateSuggestion, 'targetType' | 'targetAdId'>) {
  if (!suggestion.targetAdId) return null;
  if (suggestion.targetType === 'ROOM_AD') return `/room-ads/${suggestion.targetAdId}`;
  if (suggestion.targetType === 'ROOM_WANTED_AD') return `/room-wanted/${suggestion.targetAdId}`;
  return null;
}

export default function WhatsAppUpdateSuggestionsPanel({
  suggestions,
  loading,
  title = 'ردود واتساب على إعلانات الذكاء الاصطناعي',
  emptyText = 'لا توجد ردود واتساب معلقة للمراجعة.',
  onRefresh,
  onApply,
  onIgnore,
  busy,
}: Props) {
  return (
    <Card
      title={title}
      style={{ marginBottom: 16 }}
      extra={onRefresh ? <Button size="small" onClick={onRefresh} loading={loading}>تحديث</Button> : null}
    >
      {suggestions.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{emptyText}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {suggestions.map((suggestion) => (
            <SuggestionItem
              key={suggestion.id}
              suggestion={suggestion}
              onApply={onApply}
              onIgnore={onIgnore}
              busy={busy}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function SuggestionItem({
  suggestion,
  onApply,
  onIgnore,
  busy,
}: {
  suggestion: WhatsAppUpdateSuggestion;
  onApply: (id: number) => void;
  onIgnore: (id: number) => void;
  busy?: boolean;
}) {
  const path = adPath(suggestion);
  const changes = Object.values(suggestion.changePreview ?? {});
  return (
    <div className="ai-wa-suggestion">
      <div className="ai-wa-suggestion-body">
        <div className="ai-wa-tags">
          <Tag color={toneForStatus(suggestion.status)}>{suggestion.status}</Tag>
          {suggestion.intent ? <Tag color={suggestion.intent === 'AD_UPDATE' ? 'success' : 'default'}>{suggestion.intent}</Tag> : null}
          <Tag color={suggestion.targetType === 'ROOM_AD' ? 'info' : 'purple'}>{suggestion.targetType}</Tag>
          {path ? <Link to={path}><Tag>الإعلان #{suggestion.targetAdId}</Tag></Link> : null}
          {suggestion.phoneE164 ? <Tag>{suggestion.phoneE164}</Tag> : null}
          {suggestion.confidence != null ? <Tag color={toneForConfidence(suggestion.confidence)}>{suggestion.confidence.toFixed(2)}</Tag> : null}
        </div>
        <div className="ai-wa-message">
          {suggestion.incomingText || '-'}
        </div>
        {suggestion.ignoredReason ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 6 }}>
            <strong>سبب التجاهل:</strong> {suggestion.ignoredReason}
          </div>
        ) : null}
        {suggestion.extractedFields && Object.keys(suggestion.extractedFields).length ? (
          <div className="ai-wa-tags" style={{ marginTop: 6 }}>
            {Object.entries(suggestion.extractedFields)
              .filter(([k]) => k !== 'completeListing')
              .map(([k, v]) => <Tag key={k}>{k}: {String(v)}</Tag>)}
          </div>
        ) : null}
        {suggestion.mediaUrls && suggestion.mediaUrls.length ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 4 }}>
            مرفقات: {suggestion.mediaUrls.length}
          </div>
        ) : null}
        {changes.length ? (
          <div className="ai-wa-changes">
            <div className="ai-wa-change-head">
              <span>الحقل</span>
              <span>قبل</span>
              <span>بعد</span>
            </div>
            {changes.map((change) => (
              <div className="ai-wa-change" key={change.key}>
                <div className="ai-wa-change-label">{change.label}</div>
                <div className="ai-wa-change-value" data-label="قبل">{renderValue(change.beforeValue)}</div>
                <div className="ai-wa-change-value ai-wa-change-after" data-label="بعد">{renderValue(change.afterValue)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
            لا توجد حقول واضحة للتحديث من هذا الرد.
          </div>
        )}
        {suggestion.processingNote ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 8 }}>{suggestion.processingNote}</div>
        ) : null}
      </div>
      <Space className="ai-wa-actions">
        {path ? <Link to={path}><Button size="small">فتح الإعلان</Button></Link> : null}
        {suggestion.status === 'PENDING' ? (
          <>
            <Button size="small" type="primary" onClick={() => onApply(suggestion.id)} loading={busy}>
              اعتماد التحديث
            </Button>
            <Button size="small" onClick={() => onIgnore(suggestion.id)} disabled={busy}>
              تجاهل
            </Button>
          </>
        ) : null}
      </Space>
    </div>
  );
}

function renderValue(value: unknown) {
  if (value == null || value === '') return <span className="ai-wa-muted">-</span>;
  if (typeof value === 'object') return JSON.stringify(value);
  return <span>{String(value)}</span>;
}

function toneForConfidence(c: number | null | undefined) {
  if (c == null) return 'default';
  if (c >= 0.8) return 'success';
  if (c >= 0.55) return 'warning';
  return 'error';
}

function toneForStatus(status: string) {
  switch (status) {
    case 'PENDING': return 'success';
    case 'NEEDS_REVIEW': return 'warning';
    case 'NEEDS_REPLY': return 'info';
    case 'APPLIED': return 'info';
    default: return 'default';
  }
}
